import {useCallback, useState, type FormEvent} from 'react';
import {Banner, Button, FormLayout, Link, Text, TextField} from '@shopify/polaris';
import type {PagePropsBase} from '@slj/ui/document';

import {AuthLayout} from './auth-layout';

export interface JoinProps extends PagePropsBase {
  code: string;
  email: string | null;
  redirectTo: string;
}

/** The invite is valid — show the signup form with the code carried through as a prop. */
export function JoinPage({code, email, redirectTo}: JoinProps) {
  const [name, setName] = useState('');
  const [emailValue, setEmailValue] = useState(email ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setError(undefined);

      if (password.length < 12) {
        setError('Password must be at least 12 characters.');
        return;
      }

      setLoading(true);
      try {
        const res = await fetch('/api/auth/sign-up/email', {
          method: 'POST',
          headers: {'content-type': 'application/json'},
          credentials: 'include',
          body: JSON.stringify({
            name: name.trim(),
            email: emailValue.trim(),
            password,
            inviteCode: code,
          }),
        });
        if (res.ok) {
          window.location.href = redirectTo;
          return;
        }
        const data = (await res.json().catch(() => ({}))) as {message?: string};
        setError(data.message || 'Could not create your account.');
      } catch {
        setError('Could not reach the server. Try again.');
      }
      setLoading(false);
    },
    [name, emailValue, password, code, redirectTo],
  );

  return (
    <AuthLayout title="Set up your account">
      {error ? (
        <Banner tone="critical" onDismiss={() => setError(undefined)}>
          <p>{error}</p>
        </Banner>
      ) : null}

      <form onSubmit={onSubmit}>
        <FormLayout>
          <TextField
            label="Your name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={setName}
            requiredIndicator
            autoFocus
          />
          <TextField
            label="Email"
            type="email"
            autoComplete="username"
            value={emailValue}
            onChange={setEmailValue}
            readOnly={email !== null}
            requiredIndicator
            helpText={email ? 'This invite is tied to this address.' : undefined}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
            minLength={12}
            requiredIndicator
            helpText="At least 12 characters."
          />
          <Button submit variant="primary" fullWidth loading={loading}>
            Create account
          </Button>
        </FormLayout>
      </form>
    </AuthLayout>
  );
}

export interface JoinRejectedProps extends PagePropsBase {
  message: string;
}

/** The invite is bad. Say why — vaguely enough not to help someone guessing codes. */
export function JoinRejectedPage({message}: JoinRejectedProps) {
  return (
    <AuthLayout title="Invite not valid">
      <Text as="p" tone="subdued">
        This link can&rsquo;t be used to create an account.
      </Text>
      <Banner tone="critical">
        <p>{message}</p>
      </Banner>
      <Text as="p" tone="subdued">
        Already have an account? <Link url="/sign-in">Sign in</Link>
      </Text>
    </AuthLayout>
  );
}
